import { TraitId, TraitIds } from '@cibo/core'
import { ResponsiveDialog } from '@cibo/ui'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { propEq } from 'ramda'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraitFeature } from '..'
import { useField } from '../../..'
import { useDetailEditing } from '../../../features'

type AllDetailsTableProps = {
  resourceId?: string
}

const GenericCellDisplay = ({ traitId, year, params }: any) => {
  const detail = params.row.resolveStandingDetail(traitId, year)
  return <>{JSON.stringify(detail.result)}</>
}

export const AllDetailsTable = ({ resourceId }: AllDetailsTableProps) => {
  const { t } = useTranslation('@cibo/landmanager/components')
  const { t: traitT } = useTranslation('traits')
  const { palette } = useTheme()
  const { data: fieldModel } = useField(resourceId)
  const { setDetailModal, removeDetail } = useDetailEditing()
  const [yearDialog, setYearDialog] = useState<any>({ open: false })

  const onClickEdit = (traitId: TraitId, year?: number) => {
    if (!resourceId) {
      return
    }
    setDetailModal({
      resourceId,
      traitId,
      requirement: { traitId, year, dataType: 'field' },
    })
  }

  const onClickAdd = (traitId: TraitId) => {
    setYearDialog({ open: true, traitId })
  }

  const closeDialog = () => setYearDialog({ open: false })

  return (
    <>
      <Typography variant="h4">All Field Details</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          {TraitIds.map(traitId => {
            const Trait = TraitFeature.forTraitId(traitId)

            const CellDisplay = Trait ? Trait.cellDisplay : GenericCellDisplay

            return (
              <Fragment key={traitId}>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={3}>
                      {traitT(traitId)}{' '}
                      <pre
                        style={{
                          display: 'inline-block',
                          marginInline: 16,
                          paddingInline: 8,
                          backgroundColor: 'grey',
                        }}
                      >
                        {traitId}
                      </pre>
                    </TableCell>
                    <TableCell colSpan={1}>
                      <IconButton onClick={() => onClickAdd(traitId)}>
                        <AddIcon sx={{ color: palette.primary.contrastText }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldModel?.details.filter(propEq('traitId', traitId)).map(detail => {
                    return (
                      <TableRow key={`${detail.traitId}-${detail.year}`}>
                        <TableCell>{detail.source}</TableCell>
                        <TableCell>{detail.year}</TableCell>
                        <TableCell>
                          <CellDisplay
                            traitId={detail.traitId}
                            year={detail.year}
                            //@ts-ignore
                            params={{ row: fieldModel }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => onClickEdit(traitId, detail.year)}>
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            onClick={() =>
                              !!resourceId &&
                              removeDetail({
                                resourceId,
                                traitId,
                                year: detail.year,
                              })
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Fragment>
            )
          })}
        </Table>
      </TableContainer>

      <ResponsiveDialog open={yearDialog.open} onClose={closeDialog}>
        <DialogTitle>{t('Pick a Year')}</DialogTitle>
        <DialogContent>
          <Button
            onClick={() =>
              !!resourceId &&
              setDetailModal({
                resourceId,
                traitId: yearDialog.traitId,
              })
            }
          >
            No Year
          </Button>
          {[2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024].map(year => (
            <Button
              key={year}
              onClick={() =>
                !!resourceId &&
                setDetailModal({
                  resourceId,
                  traitId: yearDialog.traitId,
                  requirement: { traitId: yearDialog.traitId, year, dataType: 'field' },
                })
              }
            >
              {year}
            </Button>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('cancel')}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  )
}
